import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import type CrmPeopleContact from '@/types/serializers/CrmPeopleContact'
import type CrmPeopleNote from '@/types/serializers/CrmPeopleNote'
import { NotesTab } from './NotesTab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ChevronDown, ChevronRight, Mail, MoreHorizontal, Phone } from 'lucide-react'
import { toast } from 'sonner'

interface ContactCardProps {
  contact: CrmPeopleContact & { notes?: CrmPeopleNote[] }
  organizationDonorId: number
  currentUserId: number
  expanded: boolean
  onToggle: () => void
  onReloadDetail?: () => void
}

interface ContactEditFormProps {
  contact: CrmPeopleContact
  organizationDonorId: number
  onSaved: () => void
  onCancelled: () => void
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function ContactEditForm({ contact, organizationDonorId, onSaved, onCancelled }: ContactEditFormProps) {
  const { data, setData, patch, processing, errors } = useForm({
    contact: {
      first_name: contact.first_name ?? '',
      last_name: contact.last_name ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      job_title: contact.current_affiliation?.job_title ?? '',
    }
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/people/organization_donors/${organizationDonorId}/contacts/${contact.id}`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => onSaved()
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
        Modifier le contact
      </h4>
      <div className="space-y-2">
        <div>
          <Label htmlFor={`edit-first-${contact.id}`} className="text-xs">
            Prenom <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`edit-first-${contact.id}`}
            value={data.contact.first_name}
            onChange={(e) => setData('contact', { ...data.contact, first_name: e.target.value })}
            placeholder="Prenom"
            className="h-8 text-sm"
          />
          {errors['contact.first_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.first_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor={`edit-last-${contact.id}`} className="text-xs">
            Nom <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`edit-last-${contact.id}`}
            value={data.contact.last_name}
            onChange={(e) => setData('contact', { ...data.contact, last_name: e.target.value })}
            placeholder="Nom"
            className="h-8 text-sm"
          />
          {errors['contact.last_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.last_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor={`edit-email-${contact.id}`} className="text-xs">Email</Label>
          <Input
            id={`edit-email-${contact.id}`}
            type="email"
            value={data.contact.email}
            onChange={(e) => setData('contact', { ...data.contact, email: e.target.value })}
            placeholder="email@exemple.com"
            className="h-8 text-sm"
          />
          {errors['contact.email'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.email']}</p>
          )}
        </div>
        <div>
          <Label htmlFor={`edit-phone-${contact.id}`} className="text-xs">Telephone</Label>
          <Input
            id={`edit-phone-${contact.id}`}
            value={data.contact.phone}
            onChange={(e) => setData('contact', { ...data.contact, phone: e.target.value })}
            placeholder="+216 XX XXX XXX"
            className="h-8 text-sm"
          />
          {errors['contact.phone'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.phone']}</p>
          )}
        </div>
        <div>
          <Label htmlFor={`edit-job-${contact.id}`} className="text-xs">Poste</Label>
          <Input
            id={`edit-job-${contact.id}`}
            value={data.contact.job_title}
            onChange={(e) => setData('contact', { ...data.contact, job_title: e.target.value })}
            placeholder="Titre du poste"
            className="h-8 text-sm"
          />
          {errors['contact.job_title'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.job_title']}</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" size="sm" disabled={processing}>
          Enregistrer
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancelled}>
          Annuler
        </Button>
      </div>
    </form>
  )
}

export function ContactCard({
  contact,
  organizationDonorId,
  currentUserId,
  expanded,
  onToggle,
  onReloadDetail,
}: ContactCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const affiliation = contact.current_affiliation

  function handleArchive() {
    router.patch(
      `/people/organization_donors/${organizationDonorId}/contacts/${contact.id}/archive`,
      {},
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setConfirmArchive(false)
          onReloadDetail?.()
          toast.success('Contact archive avec succes')
        }
      }
    )
  }

  return (
    <>
      <div className={`border rounded-md overflow-hidden${contact.discarded ? ' opacity-60' : ''}`}>
        <button
          type="button"
          className="w-full flex items-center justify-between p-3 hover:bg-muted/50 text-left transition-colors"
          onClick={onToggle}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{contact.full_name}</p>
            <div className="flex items-center gap-3 mt-0.5">
              {affiliation?.job_title && (
                <span className="text-xs text-muted-foreground">{affiliation.job_title}</span>
              )}
              {contact.email && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {contact.email}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsEditing(true)
                    if (!expanded) onToggle()
                  }}
                >
                  Modifier
                </DropdownMenuItem>
                {!contact.discarded && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmArchive(true)
                    }}
                  >
                    Archiver
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>

        {expanded && (
          <div className="border-t p-4 space-y-4 bg-muted/20">
            {isEditing ? (
              <ContactEditForm
                contact={contact}
                organizationDonorId={organizationDonorId}
                onSaved={() => { setIsEditing(false); onReloadDetail?.() }}
                onCancelled={() => setIsEditing(false)}
              />
            ) : (
              <>
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-3">
                    Identification
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Nom complet</p>
                      <p className="text-sm">{contact.full_name}</p>
                    </div>
                    {contact.email && (
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" /> Email
                        </p>
                        <p className="text-sm">{contact.email}</p>
                      </div>
                    )}
                    {contact.phone && (
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" /> Telephone
                        </p>
                        <p className="text-sm">{contact.phone}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground">Membre depuis</p>
                      <p className="text-sm">{formatDate(contact.created_at)}</p>
                    </div>
                  </div>
                </div>

                {affiliation && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-3">
                      Affiliation actuelle
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Organisation</p>
                        <p className="text-sm">{affiliation.organization_name}</p>
                      </div>
                      {affiliation.job_title && (
                        <div>
                          <p className="text-xs text-muted-foreground">Poste</p>
                          <p className="text-sm">{affiliation.job_title}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground">Depuis</p>
                        <p className="text-sm">{formatDate(affiliation.started_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fin</p>
                        <p className="text-sm text-green-600 font-medium">Actuel</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-3">
                    Notes
                  </h4>
                  <NotesTab
                    notes={contact.notes ?? []}
                    noteableType="contact"
                    noteableId={contact.id}
                    organizationDonorId={organizationDonorId}
                    currentUserId={currentUserId}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver ce contact</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir archiver {contact.full_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
